import { FILL_PALETTE, STROKE_PALETTE, type ElementStyle } from '../scene/elements';
import type { FillStyle, FontFamily, StrokeStyle, TextAlign } from '../scene/types';
import { IconDash, IconWeight } from './icons';

interface Props {
  readonly style: ElementStyle;
  readonly hasSelection: boolean;
  readonly hasTextSelected: boolean;
  /** What is selected, shown instead of a sentence explaining the panel. */
  readonly selectionLabel: string;
  readonly onChange: (patch: Partial<ElementStyle>) => void;
}

const STROKE_WIDTHS: readonly { value: number; label: string }[] = [
  { value: 1, label: 'Thin' },
  { value: 2, label: 'Medium' },
  { value: 4, label: 'Thick' },
];

// Dash previews rather than the words "Solid / Dashed / Dotted": the control
// shows what it does.
const STROKE_STYLES: readonly { value: StrokeStyle; label: string; dash: string | undefined }[] = [
  { value: 'solid', label: 'Solid', dash: undefined },
  { value: 'dashed', label: 'Dashed', dash: '7 5' },
  { value: 'dotted', label: 'Dotted', dash: '1.5 4' },
];

const FONT_SIZES: readonly { value: number; label: string }[] = [
  { value: 13, label: 'S' },
  { value: 16, label: 'M' },
  { value: 22, label: 'L' },
  { value: 30, label: 'XL' },
];

export function StylePanel({ style, hasSelection, hasTextSelected, selectionLabel, onChange }: Props) {
  return (
    <section className="style-panel" aria-label={hasSelection ? 'Style of selection' : 'Style for new shapes'}>
      <div className="panel-head">
        <span>{hasSelection ? selectionLabel : 'Defaults'}</span>
        <span className="count">{hasSelection ? 'selected' : 'next shape'}</span>
      </div>

      <div className="field">
        <span className="label" id="stroke-label">
          Stroke
        </span>
        <div className="swatches" role="group" aria-labelledby="stroke-label">
          {STROKE_PALETTE.map((color) => (
            <button
              key={color.value}
              type="button"
              className="swatch"
              style={{ background: color.value }}
              aria-pressed={style.strokeColor === color.value}
              aria-label={`Stroke ${color.label}`}
              title={color.label}
              onClick={() => onChange({ strokeColor: color.value })}
            />
          ))}
        </div>
      </div>

      <div className="field">
        <span className="label" id="fill-label">
          Fill
        </span>
        <div className="swatches" role="group" aria-labelledby="fill-label">
          {FILL_PALETTE.map((color) => (
            <button
              key={color.value}
              type="button"
              className={`swatch${color.value === 'transparent' ? ' swatch-none' : ''}`}
              style={color.value === 'transparent' ? undefined : { background: color.value }}
              aria-pressed={style.backgroundColor === color.value}
              aria-label={`Fill ${color.label}`}
              title={color.label}
              onClick={() =>
                onChange({
                  backgroundColor: color.value,
                  fillStyle: (color.value === 'transparent' ? 'transparent' : 'solid') as FillStyle,
                })
              }
            />
          ))}
        </div>
      </div>

      <div className="field">
        <span className="label" id="width-label">
          Weight
        </span>
        <div className="seg" role="group" aria-labelledby="width-label">
          {STROKE_WIDTHS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={style.strokeWidth === option.value}
              aria-label={option.label}
              title={option.label}
              onClick={() => onChange({ strokeWidth: option.value })}
            >
              <IconWeight width={option.value === 1 ? 1 : option.value === 2 ? 2.4 : 4} />
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <span className="label" id="dash-label">
          Line
        </span>
        <div className="seg" role="group" aria-labelledby="dash-label">
          {STROKE_STYLES.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={style.strokeStyle === option.value}
              aria-label={option.label}
              title={option.label}
              onClick={() => onChange({ strokeStyle: option.value })}
            >
              <IconDash dash={option.dash} />
            </button>
          ))}
        </div>
      </div>

      {hasTextSelected || !hasSelection ? (
        <>
          <div className="field">
            <span className="label" id="size-label">
              Text size
            </span>
            <div className="seg" role="group" aria-labelledby="size-label">
              {FONT_SIZES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={style.fontSize === option.value}
                  aria-label={`Font size ${option.value} pixels`}
                  onClick={() => onChange({ fontSize: option.value })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <span className="label" id="family-label">
              Font
            </span>
            <div className="seg" role="group" aria-labelledby="family-label">
              {(['sans', 'mono'] as FontFamily[]).map((family) => (
                <button
                  key={family}
                  type="button"
                  aria-pressed={style.fontFamily === family}
                  onClick={() => onChange({ fontFamily: family })}
                >
                  {family === 'sans' ? 'Sans' : 'Mono'}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <span className="label" id="align-label">
              Text align
            </span>
            <div className="seg" role="group" aria-labelledby="align-label">
              {(['left', 'center', 'right'] as TextAlign[]).map((align) => (
                <button
                  key={align}
                  type="button"
                  aria-pressed={style.textAlign === align}
                  aria-label={`Align ${align}`}
                  onClick={() => onChange({ textAlign: align })}
                >
                  {align === 'left' ? 'L' : align === 'center' ? 'C' : 'R'}
                </button>
              ))}
            </div>
          </div>
        </>
      ) : null}

      <div className="field">
        <label className="label" htmlFor="opacity-range">
          Opacity <span style={{ color: 'var(--text)' }}>{Math.round(style.opacity * 100)}%</span>
        </label>
        <input
          id="opacity-range"
          type="range"
          min={5}
          max={100}
          step={5}
          value={Math.round(style.opacity * 100)}
          onChange={(event) => onChange({ opacity: Number(event.target.value) / 100 })}
        />
      </div>
    </section>
  );
}
